import React, { useEffect, useState } from "react";
import { RootStateOrAny, useSelector } from "react-redux";
import { Form } from "antd";
import moment from "moment";
import omit from "lodash/omit";
import { useAccountInfoContainerContext } from "../../containers/AccountInfoContainer/AccountInfoContainerContext";
import ToggleableVersionsTable from "../ToggleableVersionsTable/ToggleableVersionsTable";
import versionsTableUtils from "../../lib/utils/versionsTableUtils";
import { hasAccess } from "../RolesModule/hasAccess";
import { MENU_ACCOUNT_ACCOUNTINFO_WRITE } from "../../lib/const/permissions";
import ChangeVersionsTable from "../common/ChangeVersionsTable";

const IndividualDocumentsTable = () => {
  const { accountIdentityDocuments } = useSelector((state: RootStateOrAny) => state.accounts);
  const { docTypes, countriesTypes } = useSelector((state: RootStateOrAny) => state.dicts);
  const [tableData, setTableData] = useState<any>([]);
  const [childTableData, setChildTableData] = useState<any>([]);
  // console.log({ childTableData });
  const isChangingForbidden = versionsTableUtils.getIsTableChanging(childTableData);

  const { updateIndividualDocument } = useAccountInfoContainerContext();

  
  const [form] = Form.useForm();

  useEffect(() => {
    setTableData(
      accountIdentityDocuments.map((document, i) => ({
        ...document,
        key: document.id + i,
        docType: versionsTableUtils.geDicValueById(docTypes, document.docTypeId)?.name,
        issueCountryName: versionsTableUtils.geDicValueById(countriesTypes, document.issueCountry)
          ?.name,
        issueDate: moment(document.issueDate, "YYYY-MM-DD").format("DD.MM.YYYY"),
      }))
    );
  }, [accountIdentityDocuments, countriesTypes, docTypes]);

  // Версионности у документов не будет, поэтому данные просто из individual
  useEffect(() => {
    const currentDate = moment();
    const childData = tableData
      .map((record) => ({
        ...record,
        isCurrentVersion: moment(record.issueDate).isSameOrAfter(currentDate, "day"),
        isPastVersion: moment(record.issueDate).isBefore(currentDate, "day"),
        issueDate: moment(record.issueDate, "YYYY-MM-DD").format("DD.MM.YYYY"),
      }))
      .sort(versionsTableUtils.sortVersionsByDate);
    setChildTableData(childData);
  }, [tableData]);

  const columnsWidths = {
    ...versionsTableUtils.columnsWidths,
    docType: "10%",
    seriesNum: "10%",
    docNum: "11%",
    issueEntity: "18%",
    issueDate: "",
    issueCountryName: versionsTableUtils.columnsWidths.period,
  };

  const [columns, setColumns] = useState([
    {
      title: "Документ",
      dataIndex: "docType",
      key: "docType",
      width: columnsWidths.docType,
      hidden: false,
    },
    {
      title: "Серия",
      dataIndex: "seriesNum",
      key: "seriesNum",
      width: columnsWidths.seriesNum,
      hidden: false,
    },
    {
      title: "Номер",
      dataIndex: "docNum",
      key: "docNum",
      width: columnsWidths.docNum,
      hidden: false,
    },
    {
      title: "Выдан",
      dataIndex: "issueEntity",
      key: "issueEntity",
      width: columnsWidths.issueEntity,
      hidden: false,
    },
    {
      title: "Дата выдачи",
      dataIndex: "issueDate",
      key: "issueDate",
      width: columnsWidths.issueDate,
      hidden: false,
    },
    {
      title: "Страна",
      dataIndex: "issueCountryName",
      key: "issueCountryName",
      width: columnsWidths.issueCountryName,
      hidden: false,
    },
    {
      dataIndex: "actions",
      key: "actions",
      width: columnsWidths.actions,
      hidden: false,
    },
    {
      dataIndex: "buttons",
      key: "buttons",
      width: columnsWidths.buttons,
      hidden: false,
    },
  ]);

  const getChildColumns = () => [
    {
      title: "Документ",
      dataIndex: "docType",
      key: "docType",
      width: columnsWidths.docType,
      hidden: false,
    },
    {
      title: "Серия",
      dataIndex: "seriesNum",
      key: "seriesNum",
      width: columnsWidths.seriesNum,
      hidden: false,
      render: (v, record) => (
        <ChangeVersionsTable.Input
          name="seriesNum"
          record={record}
          setFieldsValue={form.setFieldsValue}
          required
        />
      ),
    },
    {
      title: "Номер",
      dataIndex: "docNum",
      key: "docNum",
      width: columnsWidths.docNum,
      hidden: false,
      render: (v, record) => (
        <ChangeVersionsTable.Input
          name="docNum"
          record={record}
          setFieldsValue={form.setFieldsValue}
          required
        />
      ),
    },
    {
      title: "Выдан",
      dataIndex: "issueEntity",
      key: "issueEntity",
      width: columnsWidths.issueEntity,
      hidden: false,
      render: (v, record) => (
        <ChangeVersionsTable.Input
          name="issueEntity"
          record={record}
          setFieldsValue={form.setFieldsValue}
          required
        />
      ),
    },
    {
      title: "Дата выдачи",
      dataIndex: "issueDate",
      key: "issueDate",
      width: columnsWidths.issueDate,
      hidden: false,
    },
    {
      title: "Страна",
      dataIndex: "issueCountryName",
      key: "issueCountryName",
      width: columnsWidths.issueCountryName,
      hidden: false,
    },
    {
      title: "",
      dataIndex: "actions",
      key: "actions",
      width: columnsWidths.actions,
      hidden: false,
      render: (value, record) => {
        const isHasAccess = hasAccess(MENU_ACCOUNT_ACCOUNTINFO_WRITE);
        return (
          <>
            <ChangeVersionsTable.AddButton
              hasAccess={isHasAccess}
              isChangingForbidden={isChangingForbidden}
              record={record}
              setChildTableData={setChildTableData}
            />
            <ChangeVersionsTable.EditButton
              hasAccess={isHasAccess}
              isChangingForbidden={isChangingForbidden}
              record={record}
              setChildTableData={setChildTableData}
            />
            <ChangeVersionsTable.SubmitButton record={record} submit={form.submit} />
            <ChangeVersionsTable.CancelButton
              record={record}
              resetFields={form.resetFields}
              setChildTableData={setChildTableData}
            />
          </>
        );
      },
    },
    {
      dataIndex: "buttons",
      key: "buttons",
      width: columnsWidths.buttons,
      hidden: false,
    },
  ];

  const [childColumns, setChildColumns] = versionsTableUtils.useChildColumns(getChildColumns, [
    isChangingForbidden,
  ]);

  const onFinish = (values) => {
    const editableRecord = versionsTableUtils.getChangeableRecord(childTableData);
    if (editableRecord.isEditing) {
      updateIndividualDocument({
        document: {
          ...omit(editableRecord, "key", "isCurrentVersion", "isPastVersion"),
          ...values,
        },
      });
    }
  };

  return (
    <Form form={form} onFinish={onFinish}>
      <h4 style={{ marginTop: "45px" }}>Документ</h4>
      <ToggleableVersionsTable
        tableName="individual-documents-table"
        columns={columns}
        setColumns={setColumns}
        childTableColumns={childColumns}
        setChildTableColumns={setChildColumns}
        tableData={tableData}
        childTableData={childTableData}
        isChildTableLoading={false}
        tableClassName="individual-documents-table"
        isPageable={false}
        isToggleableColumns={false}
      />
    </Form>
  );
};

export default IndividualDocumentsTable;
